import { ConversationType, EmailDeliveryStatus, EmailDirection } from '@/types/api';
import type { Attachment, Conversation, EmailDelivery } from '@/types/api';

export type ConversationKind = 'reply' | 'note' | 'activity';

const activityConversationTypes = new Set<ConversationType>([
  ConversationType.Activity,
  ConversationType.StatusChange,
  ConversationType.Assignment,
  ConversationType.PriorityChange,
  ConversationType.Escalation,
]);

export const normaliseConversation = (conversation: Conversation): Conversation => ({
  ...conversation,
  conversationType: conversation.type ?? conversation.conversationType,
  isInternal: conversation.isInternal ?? conversation.isPublic === false,
  user: conversation.author || conversation.user,
});

export const getConversationKind = (conversation: Conversation): ConversationKind => {
  const type = conversation.type ?? conversation.conversationType;
  if (activityConversationTypes.has(type)) return 'activity';
  if (type === ConversationType.Note) return 'note';
  return 'reply';
};

export const getConversationLabel = (conversation: Conversation): string => {
  const type = conversation.type ?? conversation.conversationType;
  switch (type) {
    case ConversationType.Note: return 'Internal Note';
    case ConversationType.StatusChange: return 'Status Change';
    case ConversationType.Assignment: return 'Assignment';
    case ConversationType.PriorityChange: return 'Priority Change';
    case ConversationType.Escalation: return 'Escalation';
    case ConversationType.Activity: return 'System Activity';
    default:
      if (conversation.emailDirection === EmailDirection.Inbound) return 'Inbound Email';
      if (conversation.emailDirection === EmailDirection.Outbound) return 'Outbound Public Reply';
      return 'Public Reply';
  }
};

export const getLatestEmailDelivery = (conversation: Conversation): EmailDelivery | undefined => {
  if (!conversation.emailDeliveries?.length) return undefined;

  const sortedDeliveries = [...conversation.emailDeliveries].sort((left, right) => {
    const leftTime = new Date(left.processedAt || left.receivedAt).getTime();
    const rightTime = new Date(right.processedAt || right.receivedAt).getTime();
    return leftTime - rightTime;
  });
  return sortedDeliveries[sortedDeliveries.length - 1];
};

export const getEmailDeliveryPresentation = (status: EmailDeliveryStatus): {
  label: string;
  variant: 'secondary' | 'info' | 'success' | 'warning' | 'error';
} => {
  switch (status) {
    case EmailDeliveryStatus.Queued:
      return { label: 'Queued', variant: 'secondary' };
    case EmailDeliveryStatus.Sent:
      return { label: 'Sent', variant: 'info' };
    case EmailDeliveryStatus.Failed:
      return { label: 'Failed', variant: 'error' };
    case EmailDeliveryStatus.Bounced:
      return { label: 'Bounced', variant: 'warning' };
    case EmailDeliveryStatus.Delivered:
      return { label: 'Delivered', variant: 'success' };
    default:
      return { label: 'Unknown', variant: 'secondary' };
  }
};

export const getAttachmentDisplayName = (attachment: Attachment): string =>
  attachment.displayName || attachment.originalFileName || attachment.fileName || 'Attachment';

export const getAttachmentSize = (attachment: Attachment): number =>
  attachment.sizeBytes ?? attachment.fileSize ?? 0;
